import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Users, UserPlus, Calendar, Search, Home, Menu, X, LogOut } from 'lucide-react';
import './App.css';

// Import components
import Dashboard from './components/Dashboard';
import PatientRegistration from './components/PatientRegistration';
import PatientSearch from './components/PatientSearch';
import PatientProfile from './components/PatientProfile';
import NewVisit from './components/NewVisit';
import Login from './components/Login';
import VisitEditForm from './components/VisitEditForm'; // Already imported correctly
import ProtectedRoute from './components/ProtectedRoute';

// Import services
import apiService from './services/api';
import authService from './services/authService';

function App() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Load initial data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  // Session timeout check
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        if (!authService.isAuthenticated()) {
          handleLogout();
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load recent patients for dashboard
      const recentPatients = await apiService.getRecentPatients();
      setPatients(recentPatients);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (authStatus) => {
    setIsAuthenticated(authStatus);
    if (authStatus) {
      authService.login();
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setPatients([]);
    setMobileMenuOpen(false);
    setError(null);
  };

  const addPatient = async (newPatientData) => {
    try {
      const createdPatient = await apiService.createPatient(newPatientData);
      
      // Add to local state
      setPatients(prev => [createdPatient, ...prev]);
      
      return createdPatient;
    } catch (err) {
      console.error('Failed to create patient:', err);
      throw err;
    }
  };

  const addVisit = async (patientId, visitData) => {
    try {
      const createdVisit = await apiService.createVisit(patientId, visitData);
      
      // Update local patient data
      setPatients(prev => prev.map(patient => 
        patient.id === patientId 
          ? { ...patient, visits: [...(patient.visits || []), createdVisit] }
          : patient
      ));
      
      return createdVisit;
    } catch (err) {
      console.error('Failed to create visit:', err);
      throw err;
    }
  };

  const updatePatient = async (patientId, updatedData) => {
    try {
      const updatedPatient = await apiService.updatePatient(patientId, updatedData);
      
      // Update local state
      setPatients(prev => prev.map(patient => 
        patient.id === patientId ? updatedPatient : patient
      ));
      
      return updatedPatient;
    } catch (err) {
      console.error('Failed to update patient:', err);
      throw err;
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-sm sm:text-base">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ProtectedRoute isAuthenticated={isAuthenticated} onLogout={handleLogout}>
        <div className="min-h-screen bg-gray-50">
          {/* Navigation */}
          <nav className="bg-blue-600 text-white shadow-lg sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-3 sm:py-4">
                {/* Logo and Title */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8" />
                  <h1 className="text-sm sm:text-lg lg:text-xl font-bold truncate">
                    <span className="hidden sm:inline">Nova Dent Dental Patient Management</span>
                    <span className="sm:hidden">Nova Dent</span>
                  </h1>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
                  <Link 
                    to="/" 
                    className="flex items-center space-x-1 hover:bg-blue-700 px-2 lg:px-3 py-2 rounded transition-colors duration-200"
                  >
                    <Home className="h-4 w-4" />
                    <span className="text-sm lg:text-base">Dashboard</span>
                  </Link>
                  <Link 
                    to="/register" 
                    className="flex items-center space-x-1 hover:bg-blue-700 px-2 lg:px-3 py-2 rounded transition-colors duration-200"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span className="text-sm lg:text-base">New Patient</span>
                  </Link>
                  <Link 
                    to="/search" 
                    className="flex items-center space-x-1 hover:bg-blue-700 px-2 lg:px-3 py-2 rounded transition-colors duration-200"
                  >
                    <Search className="h-4 w-4" />
                    <span className="text-sm lg:text-base">Find Patient</span>
                  </Link>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 hover:bg-blue-700 px-2 lg:px-3 py-2 rounded transition-colors duration-200 border-l border-blue-500 ml-2 pl-4"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm lg:text-base">Logout</span>
                  </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="md:hidden p-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                  aria-label="Toggle mobile menu"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>

              {/* Mobile Navigation Menu */}
              {mobileMenuOpen && (
                <div className="md:hidden border-t border-blue-500 py-2">
                  <div className="flex flex-col space-y-1">
                    <Link 
                      to="/" 
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-3 rounded transition-colors duration-200"
                    >
                      <Home className="h-5 w-5" />
                      <span>Dashboard</span>
                    </Link>
                    <Link 
                      to="/register" 
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-3 rounded transition-colors duration-200"
                    >
                      <UserPlus className="h-5 w-5" />
                      <span>New Patient</span>
                    </Link>
                    <Link 
                      to="/search" 
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-3 rounded transition-colors duration-200"
                    >
                      <Search className="h-5 w-5" />
                      <span>Find Patient</span>
                    </Link>
                    
                    {/* Mobile Logout */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 hover:bg-blue-700 px-3 py-3 rounded transition-colors duration-200 border-t border-blue-500 mt-2 pt-4"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Overlay */}
          {mobileMenuOpen && (
            <div 
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={closeMobileMenu}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <p className="text-sm sm:text-base mb-2">Error: {error}</p>
                <button 
                  onClick={loadDashboardData}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm sm:text-base"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
            <Routes>
              <Route path="/" element={<Dashboard patients={patients} />} />
              <Route path="/register" element={<PatientRegistration onAddPatient={addPatient} />} />
              <Route path="/search" element={<PatientSearch />} />
              <Route path="/patient/:id" element={<PatientProfile onAddVisit={addVisit} onUpdatePatient={updatePatient} />} />
              <Route path="/patient/:id/visit" element={<NewVisit onAddVisit={addVisit} />} />
              
              {/* === MISSING ROUTE ADDED HERE === */}
              <Route 
                path="/patient/:id/visit/:visitId/edit" 
                element={<VisitEditForm />} 
              />
              {/* ================================ */}
            
            </Routes>
          </main>
        </div>
      </ProtectedRoute>
    </Router>
  );
}

export default App;